import { z } from "zod";
import type { Column, ColumnPin } from "@common/types/views/views";
import { getSavedVariantCandidates } from "@helpers/viewColumnVariant";
import {
    type DataGridLeafColumnInfo,
    getDefaultColumnState,
} from "../columns/columnLayout";
import type { DataGridPin } from "../columns/types";
import type { DataGridColumnState } from "../types";
import { getValueIds } from "./savedViewColumns";

const getDisplayPins = (
    leaves: readonly DataGridLeafColumnInfo[],
    pin: DataGridPin,
) =>
    leaves
        .filter((leaf) => leaf.isDisplay && leaf.pin === pin)
        .map((leaf) => leaf.id);

/**
 * Shown ids in their order, with each hidden id placed right after the column
 * before it in `referenceOrder` (first when none is), so a column hidden and
 * shown again comes back where it was.
 */
const placeHiddenIds = ({
    shownIds,
    hiddenIds,
    referenceOrder,
}: {
    shownIds: readonly string[];
    hiddenIds: readonly string[];
    referenceOrder: readonly string[];
}) => {
    const orderedIds = [...shownIds];
    const referenceIds = [
        ...referenceOrder.filter(
            (id) => shownIds.includes(id) || hiddenIds.includes(id),
        ),
        ...hiddenIds.filter((id) => !referenceOrder.includes(id)),
    ];
    referenceIds.forEach((id, index) => {
        if (!hiddenIds.includes(id)) {
            return;
        }
        const previous = referenceIds
            .slice(0, index)
            .findLast((candidate) => orderedIds.includes(candidate));
        orderedIds.splice(
            previous === undefined ? 0 : orderedIds.indexOf(previous) + 1,
            0,
            id,
        );
    });
    return orderedIds;
};

/** ViewTable mirrors `customVariants.variant` here; `Column["options"]` doesn't declare it. */
const LegacyVariantSchema = z.object({ variant: z.string() });

type LeafVariants = NonNullable<DataGridLeafColumnInfo["variants"]>;

/** The first saved variant the column offers, read as ViewTable reads it. */
const readVariant = (column: Column, variants: LeafVariants) => {
    const legacy = LegacyVariantSchema.safeParse(column.options);
    return getSavedVariantCandidates({
        customVariants: column.options?.customVariants,
        legacyVariant: legacy.success ? legacy.data.variant : undefined,
        optionKey: variants.key,
    }).find(
        (candidate): candidate is string =>
            typeof candidate === "string" &&
            variants.values.includes(candidate),
    );
};

/** Each shown column's saved variant, where ViewTable's `variantMenu` keeps it. */
const readVariants = ({
    shownColumns,
    leaves,
}: {
    shownColumns: readonly Column[];
    leaves: readonly DataGridLeafColumnInfo[];
}) => {
    const variantsById = new Map(
        leaves.map((leaf) => [leaf.id, leaf.variants]),
    );
    return Object.fromEntries(
        shownColumns.flatMap((column) => {
            const variants = variantsById.get(column.id);
            const value = variants && readVariant(column, variants);
            return value === undefined ? [] : [[column.id, value]];
        }),
    );
};

interface ViewColumnsToColumnStateInput {
    viewColumns: readonly Column[];
    leaves: readonly DataGridLeafColumnInfo[];
    /** Where hidden columns sit; defaults to definition order. */
    referenceOrder?: readonly string[];
    /** What a view with no known columns shows; defaults to the definitions'. */
    fallback?: DataGridColumnState;
}

/**
 * A saved view's columns as table column state. The view lists the shown
 * value columns in display order, with `options.size` and `options.pin`;
 * value columns it omits are hidden, placed by `referenceOrder`. Display
 * columns (select, actions) keep their definition slot and pin, but a value
 * column's `meta.pin` only applies through `fallback` or the definitions'
 * defaults: the view's own pins win. A view with no known columns shows
 * `fallback`.
 */
export const viewColumnsToColumnState = ({
    viewColumns,
    leaves,
    referenceOrder,
    fallback,
}: ViewColumnsToColumnStateInput): DataGridColumnState => {
    const valueIds = getValueIds(leaves);
    const knownIds = new Set(valueIds);
    const shownColumns = viewColumns.filter((column) =>
        knownIds.has(column.id),
    );
    if (shownColumns.length === 0) {
        return fallback ?? getDefaultColumnState(leaves);
    }
    const shownIds = new Set(shownColumns.map((column) => column.id));
    const orderedValueIds = placeHiddenIds({
        shownIds: shownColumns.map((column) => column.id),
        hiddenIds: valueIds.filter((id) => !shownIds.has(id)),
        referenceOrder: referenceOrder ?? valueIds,
    });
    let nextValue = 0;
    const pinnedTo = (pin: ColumnPin) =>
        shownColumns
            .filter((column) => column.options?.pin === pin)
            .map((column) => column.id);
    return {
        order: leaves.map((leaf) =>
            leaf.isDisplay ? leaf.id : orderedValueIds[nextValue++],
        ),
        sizing: Object.fromEntries(
            shownColumns.flatMap((column) =>
                column.options?.size === undefined
                    ? []
                    : [[column.id, column.options.size]],
            ),
        ),
        visibility: Object.fromEntries(
            valueIds.map((id) => [id, shownIds.has(id)]),
        ),
        pinning: {
            start: [...getDisplayPins(leaves, "start"), ...pinnedTo("left")],
            end: [...pinnedTo("right"), ...getDisplayPins(leaves, "end")],
        },
        variants: readVariants({ shownColumns, leaves }),
    };
};
